<template>
  <Dialog
    :visible="!!mode"
    modal
    class="marketing-prompt"
    :header="header"
    :style="{width: '95vw', maxWidth: '580px'}"
    @update:visible="(visible) => { if (!visible) $emit('close') }"
  >
    <p v-if="mode === 'published'" class="marketing-prompt__success">
      <Icon icon="fa-pro-solid:circle-check" />
      <span><strong>{{ displayName }}</strong> ist jetzt auf Ihrer Website online.</span>
    </p>

    <h4 v-if="mode === 'published'" class="marketing-prompt__question">Auch auf mvp.de zeigen?</h4>
    <p class="marketing-prompt__intro">
      Zusätzlich zu Ihrer Website kann der Eintrag auf mvp.de erscheinen. So finden ihn auch Gäste,
      die Ihre Website noch nicht kennen.
    </p>

    <section v-if="currentPackage" class="marketing-prompt__contingent">
      <div class="marketing-prompt__contingent-title">
        Ihr mvp.de-Kontingent <span class="marketing-prompt__package">{{ packageLabel }}</span>
      </div>
      <p class="marketing-prompt__contingent-note">
        Auf Ihrer Website können Sie unbegrenzt veröffentlichen. Nur Einträge auf mvp.de belegen einen Platz.
      </p>

      <p v-if="loading" class="marketing-prompt__contingent-state mb-0">Kontingent wird geladen …</p>
      <p v-else-if="marketedCount === null" class="marketing-prompt__contingent-state mb-0">
        Ihr Kontingent konnte gerade nicht geladen werden.
      </p>
      <template v-else>
        <ProgressBar
          :class="{warning: progress > 0.8 && progress < 1, danger: progress >= 1}"
          :value="progress * 100"
        >
          {{ marketedCount }} von {{ quantity }} belegt
        </ProgressBar>
        <div v-if="exhausted" class="alert alert-warning mt-3 mb-0" role="status">
          Alle {{ placesLabel(quantity) }} auf mvp.de sind belegt. Nehmen Sie zuerst einen anderen Eintrag von
          mvp.de oder erweitern Sie Ihr Paket.
          <router-link :to="{name: 'packages.index', query: routeQuery}" class="marketing-prompt__link">
            Paketoptionen ansehen
          </router-link>
        </div>
        <p v-else class="marketing-prompt__contingent-state mt-2 mb-0">
          Dieser Eintrag belegt einen Platz. Danach {{ remainingAfterLabel }}.
        </p>
      </template>
    </section>

    <template #footer>
      <button type="button" class="btn btn-outline-secondary" @click="$emit('close')">
        {{ mode === 'published' ? 'Nein, nur auf meiner Website' : 'Abbrechen' }}
      </button>
      <button
        type="button"
        class="btn btn-primary marketing-prompt__confirm"
        :disabled="busy || loading || exhausted"
        @click="$emit('confirm')"
      >
        Ja, auch auf mvp.de zeigen
      </button>
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog'
import ProgressBar from 'primevue/progressbar'
import {useObjectStore} from '@/stores/object'
import {useUserStore} from '@/stores/user'
import {
  contingentQuantity,
  countMarketedObjects,
  findBaseTypePackage,
  packageTypeLabel,
  placesLabel,
} from '@/utils/marketingContingent'

export default {
  name: 'MarketingPromptDialog',
  components: {Dialog, ProgressBar},
  emits: ['confirm', 'close'],
  props: {
    // null (closed), 'published' (asked right after website publication) or 'confirm' (asked on request).
    mode: {
      type: String,
      default: null,
    },
    objectName: {
      type: String,
      default: '',
    },
    packageBaseType: {
      type: String,
      default: null,
    },
    busy: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    return {
      objectStore: useObjectStore(),
      userStore: useUserStore(),
    }
  },
  data() {
    return {
      loading: false,
      marketedCount: null,
    }
  },
  computed: {
    header() {
      return this.mode === 'published' ? 'Auf Ihrer Website veröffentlicht' : 'Auch auf mvp.de zeigen?'
    },
    displayName() {
      const name = String(this.objectName || '').trim()
      return name ? `„${name}“` : 'Ihr Eintrag'
    },
    currentPackage() {
      return findBaseTypePackage(this.userStore.user?.organisation?.active_packages, this.packageBaseType)
    },
    packageLabel() {
      return `${packageTypeLabel(this.currentPackage)} · ${placesLabel(this.quantity)}`
    },
    quantity() {
      return contingentQuantity(this.currentPackage)
    },
    progress() {
      return this.quantity ? Math.min(this.marketedCount / this.quantity, 1) : 1
    },
    exhausted() {
      return this.marketedCount !== null && this.marketedCount >= this.quantity
    },
    remainingAfterLabel() {
      const remaining = this.quantity - this.marketedCount - 1
      if (remaining === 0) {
        return 'ist Ihr Kontingent voll ausgeschöpft'
      }
      return remaining === 1 ? 'ist noch 1 Platz frei' : `sind noch ${remaining} Plätze frei`
    },
    routeQuery() {
      return this.$route?.query || {}
    },
  },
  watch: {
    mode: {
      immediate: true,
      handler(mode) {
        if (mode) {
          this.loadMarketedCount()
        }
      },
    },
  },
  methods: {
    placesLabel,
    async loadMarketedCount() {
      if (!this.currentPackage) {
        return
      }

      this.loading = true
      this.marketedCount = null
      try {
        const {data} = await this.objectStore.apiIndex(this.packageBaseType)
        this.marketedCount = countMarketedObjects(Array.isArray(data) ? data : data?.objects)
      } catch (error) {
        console.error('Loading the mvp.de contingent failed', error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss">
.marketing-prompt__success {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  border-left: 4px solid #1f7a55;
  background: #eef8f3;
  color: #14482f;

  svg {
    flex-shrink: 0;
    margin-top: 0.2rem;
    color: #1f7a55;
  }
}

.marketing-prompt__question {
  margin: 1.25rem 0 0.5rem;
  font-size: 1.1rem;
  font-weight: 700;
}

.marketing-prompt__contingent {
  padding: 1rem;
  border: 1px solid #d8e1ea;
  background: #f8fbfd;
}

.marketing-prompt__contingent-title {
  color: #10273d;
  font-weight: 700;
}

.marketing-prompt__package {
  margin-left: 0.35rem;
  color: #5d7183;
  font-size: 0.85rem;
  font-weight: 400;
}

.marketing-prompt__contingent-note,
.marketing-prompt__contingent-state {
  color: #4e6477;
  font-size: 0.9rem;
}

.marketing-prompt__link {
  margin-left: 0.35rem;
  color: inherit;
  font-weight: 700;
}
</style>
